/** @jsx jsx */
import { connect } from 'react-redux';

import {
  Alert,
  AlertDescription,
  Button,
  Spacer,
  Text,
  Typography,
} from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import Link from 'app/common/components/Link';
import WistiaVideo from 'app/developer-v3/components/WistiaVideo';
import { BASE_DOC_URL_VB } from 'app/developer-v3/constants';
import {
  createPartnerUserInteractionEventTracker,
  PartnerUserInteractionEventTracker,
} from 'app/developer-v3/teams/team-partner-sharing/createInteractionTracker';
import { State } from 'app/common/types';

const styleColumns = css`
  --width: 300px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(var(--width), 1fr));
  column-gap: 25px;
  row-gap: 32px;
`;

const styleFigcaption = css`
  margin-top: 20px;
`;

const stylePage = css`
  display: flex;
  flex-direction: column;
  padding: 25px 45px;

  & b {
    font-weight: 700;
  }

  & p {
    ${Typography.Body1}
  }
`;

type Props = {
  trackInteraction: PartnerUserInteractionEventTracker;
};

function EmbedAutomaticAccountCreation({ trackInteraction }: Props) {
  return (
    <div css={stylePage}>
      <Text margin="0 0 25px" tag="h1" type="PageHeader">
        Automatic Account Creation
      </Text>
      <p>
        Save steps and reduce friction when your users sign up for Zapier so
        they can start automating workflows with your integration quicker.
        Enabling this feature will allow a Zapier account to be created on their
        behalf when they first click on one of the Zapier embeds from within
        your product. Users no longer need to worry about the extra step of
        signing up for Zapier.
      </p>
      <Spacer height={5} />
      <Alert title="Have you already embedded Zapier?">
        <AlertDescription>
          This feature can be used with any Zapier experience you’ve already
          embedded. Once Automatic Account Creation is enabled, first-time
          Zapier users will be automatically routed through the new sign-up
          process when they set up Zaps inside your product.
        </AlertDescription>
      </Alert>
      <Spacer height={15} />
      <h2>How it works</h2>
      <div css={styleColumns}>
        <figure>
          <WistiaVideo
            popover={true}
            popoverCaption="Without Automatic Account Creation"
            wistiaID="tqz0movaqf"
          />
          <figcaption css={styleFigcaption}>
            <h3>Without Automatic Account Creation</h3>
            <p>
              If your user does not have a Zapier account, they will need to
              create one through the normal sign up process when setting up a
              Zap with your integration.
            </p>
          </figcaption>
        </figure>

        <figure>
          <WistiaVideo
            popover={true}
            popoverCaption="After enabling Automatic Account Creation"
            wistiaID="e66sqvvd20"
          />
          <figcaption css={styleFigcaption}>
            <h3>After enabling Automatic Account Creation</h3>
            <p>
              If your user does not have a Zapier account, we will automatically
              create one for them using the same account information they have
              in your app.
            </p>
          </figcaption>
        </figure>
      </div>
      <h2>Requirements</h2>
      <p>
        Check the{' '}
        <Link
          href={
            new URL(
              '/embed/automatic-account-creation#requirements',
              BASE_DOC_URL_VB
            ).href
          }
          target="_blank"
        >
          requirements
        </Link>{' '}
        to see if you can enable Automatic Account Creation.
      </p>
      <h2>Submit your configuration details</h2>
      <p>
        Provide us with your IDP (Identity Provider) configuration details and
        we will enable Automatic Account Creation for your users. You can also
        learn more about the{' '}
        <Link
          href={
            new URL('/embed/automatic-account-creation', BASE_DOC_URL_VB).href
          }
          target="_blank"
        >
          configuration details
        </Link>{' '}
        or contact{' '}
        <Link href="mailto:partners@zapier.com">partners@zapier.com</Link>.
      </p>
      <Spacer height={30} />
      <div>
        <Button
          href="https://zapier.typeform.com/to/OlPloIcW/?utm_source=zapier_marketing_website&utm_medium=embed_experience&utm_campaign=embed_tooling_page"
          onClick={() => {
            trackInteraction({
              eventAction: 'click',
              interactionName: 'submit_idp_details',
              interactionGoal:
                'Submit IDP details to configure Automatic Account Creation',
            });
          }}
          target="_blank"
        >
          Submit IDP details
        </Button>
      </div>
    </div>
  );
}

function mapStateToProps(state: State): Partial<Props> {
  const trackInteraction = createPartnerUserInteractionEventTracker(state);
  return { trackInteraction };
}

export default connect(mapStateToProps)(EmbedAutomaticAccountCreation);
